import type { ClientMetadata } from 'magpie-types';
import type { Tag } from 'magpie-types/tags';
import { ConfigManagerError } from '../../errors/config-manager.error';
import {
  safeGetPortalId,
  safeGetProjectName,
  shallowCopy,
  getOrCreateFrameId,
  labelsMapToJsonSerializableRecord,
  mergeLabelValues,
} from '../../utils';
import type { ConfigProperties, ConfigKey } from './config-properties';
import { MAGPIE_CLIENT_LOGGER_NAME } from '../../config/constants';
import { Logger } from 'simple-logging-lib';
export class MagpieConfigManager {
  private readonly logger = Logger.getLogger(MAGPIE_CLIENT_LOGGER_NAME);

  static #instance: MagpieConfigManager;

  #locked = false;
  #config: ConfigProperties = {
    portalId: safeGetPortalId(),
    appName: safeGetProjectName() || 'unknown',
    tags: [],
    labels: new Map(),
    strategy: undefined,
    debug: false,
    forceReload: false,
    onConnectionError: this.logger.error,
  };

  private constructor(overrides: Partial<ConfigProperties>) {
    this.#config = { ...this.#config, ...overrides };
  }

  public static getInstance(
    overrides: Partial<ConfigProperties> = {}
  ): MagpieConfigManager {
    if (!MagpieConfigManager.#instance) {
      MagpieConfigManager.#instance = new MagpieConfigManager(overrides);
    }
    return MagpieConfigManager.#instance;
  }

  public get<K extends ConfigKey>(key: K): ConfigProperties[K] {
    // Return a shallow copy if required to prevent direct mutation
    return shallowCopy(this.#config[key]);
  }

  public set<K extends ConfigKey>(key: K, value: ConfigProperties[K]): void {
    this.checkLock();
    this.#config[key] = shallowCopy(value);
  }

  public setDebugMode(debugMode: boolean): void {
    // Allow setting debug mode after the config is locked
    this.#config.debug = debugMode;
  }

  public addTag(tag: Tag): void {
    this.checkLock();
    if (!this.#config.tags.includes(tag)) {
      this.#config.tags = [...this.#config.tags, tag];
    }
  }

  public addLabel(key: string, values: string[] | string): void {
    this.checkLock();
    this.#config.labels = mergeLabelValues(this.#config.labels, key, values);
  }

  public getConfig = (): ConfigProperties => shallowCopy(this.#config);

  public getClientMetadata = (): ClientMetadata => ({
    portalId: this.get('portalId')?.toString(),
    appName: this.get('appName'),
    tags: this.get('tags'),
    labels: labelsMapToJsonSerializableRecord(this.get('labels')),
    frameId: getOrCreateFrameId(),
  });

  public lock(): void {
    this.#locked = true;
  }

  private checkLock(): void {
    if (this.#locked) {
      throw new ConfigManagerError(ConfigManagerError.Reason.LOCKED);
    }
  }

  public static resetInstance(): void {
    MagpieConfigManager.#instance = undefined as any;
  }
}
